import * as React from 'react';
import {
  Dispatch,
  FC,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import { ModalItemUnavailable } from 'components/modal-item-unavailable/modal-item-unavailable';
import useDialogModal, { DialogCb } from 'hooks/use-dialog-modal';
import { useMenuContext } from 'state/menu';
import { StoreProxy } from 'state/store';
import { routes } from 'utils/routing';

export interface IStaticMenuContext {
  setSelectedStaticMenuItem: Dispatch<SetStateAction<{ itemId: string; itemName: string }>>;
  selectedStaticMenuItemName: string;
  selectedStaticMenuItemId: string;
  selectedStaticMenuItemSearch: string;
  OpenItemUnavailableModal: DialogCb<object>;
  checkStaticMenuItemAvailability(store: StoreProxy): Promise<void>;
}

export const StaticMenuContext = createContext({} as IStaticMenuContext);

export const useStaticMenuContext = () => useContext(StaticMenuContext);

/**
 *
 * Controls showing of static menu and redirect flow for static menu ordering.
 *
 * If a user selects a menu item in the static menu with no store selected
 * the item pathname they selected is stored in "selectedStaticMenuItemId" and they are
 * navigated to store selector.
 *
 * After selection of a store, the user is routed back to the "selectedStaticMenuItemId".
 *
 **/

const DEFAULT_STATE = {
  itemId: '',
  itemName: '',
  search: ''
};

export const StaticMenuProvider: FC = ({ children }) => {
  const { checkItemAvailability } = useMenuContext();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [{ itemId, itemName, search }, setSelectedStaticMenuItem] = useState(DEFAULT_STATE);
  const [ItemUnavailableModal, OpenItemUnavailableModal] = useDialogModal({
    Component: ModalItemUnavailable
  });
  useEffect(() => {
    if (itemId) {
      const notInStaticMenuRedirectFlow =
        pathname !== `${routes.menu}/${itemId}` && !pathname.includes(routes.storeLocator);
      if (notInStaticMenuRedirectFlow) {
        setSelectedStaticMenuItem(DEFAULT_STATE);
      }
    }
  }, [itemId, pathname]);

  const checkStaticMenuItemAvailability = useCallback(
    async (restaurant: StoreProxy) => {
      const isAvailable = await checkItemAvailability({
        posVendor: restaurant?.pos?.vendor?.toLowerCase() ?? '',
        restaurantId: restaurant?.restaurantPosData?._id ?? '',
        itemId
      });
      if (isAvailable) {
        navigate(`${routes.menu}/${itemId}${search}`);
      } else {
        OpenItemUnavailableModal();
      }
    },
    [checkItemAvailability, itemId, navigate, search, OpenItemUnavailableModal]
  );

  const value = useMemo(
    () => ({
      setSelectedStaticMenuItem,
      selectedStaticMenuItemId: itemId,
      selectedStaticMenuItemName: itemName,
      selectedStaticMenuItemSearch: search,
      OpenItemUnavailableModal,
      checkStaticMenuItemAvailability
    }),
    [OpenItemUnavailableModal, checkStaticMenuItemAvailability, itemId, itemName, search]
  );

  return (
    <StaticMenuContext.Provider value={value}>
      {children}
      <ItemUnavailableModal />
    </StaticMenuContext.Provider>
  );
};
